import { useBreakpointDown } from '@all-manager/design-system-v2';
import { useQueryClient } from '@tanstack/react-query';
import { type MouseEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSpace } from '../../../../hooks/useSpace';
import { useSpacePath } from '../../../../hooks/useSpacePath';
import { useGetSpaceAISettings } from '../../../inbox/api/useGetSpaceAISettings';
import { keys } from '../../api/keys';
import { useAskAssistant } from '../../api/useAskAssistant';
import { useGetAiConversation } from '../../api/useGetAiConversation';
import { useGetAiConversationMessages } from '../../api/useGetAiConversationMessages';
import { useGetAiConversations } from '../../api/useGetAiConversations';
import type { AiChatMessage, AiConversation } from '../../types/aiChat.types';

export const SUGGESTED_QUESTIONS = [
  'Quand est prévue la prochaine AG ?',
  'Quand a eu lieu le ravalement de façade ?',
  'Quels contrats avons-nous avec nos prestataires ?',
];

const NEW_CONVERSATION_TITLE = 'Nouvelle conversation';
// The phone header stays pinned: keep it to one or two lines.
const COMPACT_TITLE_MAX_LENGTH = 40;

export function shortenTitle(title: string, maxLength: number): string {
  return title.length > maxLength ? `${title.slice(0, maxLength - 1).trimEnd()}…` : title;
}

function createMessage(role: AiChatMessage['role'], text: string, sources?: AiChatMessage['sources']): AiChatMessage {
  return { id: crypto.randomUUID(), role, text, sources };
}

export function useAiChatPage() {
  const { currentSpace } = useSpace();
  const params = useParams<{ conversationId?: string }>();
  const conversationId = params.conversationId;
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const queryClient = useQueryClient();
  const isCompact = useBreakpointDown('md');

  const { data: isAiEnabled = true, isLoading: isSettingsLoading } = useGetSpaceAISettings({
    spaceId: currentSpace?.id,
  });
  const { data: conversations } = useGetAiConversations();
  // Looked up on its own: the history list is capped and may not be refetched yet.
  const { data: conversation, isLoading: isConversationLoading } = useGetAiConversation(conversationId);
  const { data: messages = [], isLoading: isMessagesLoading } = useGetAiConversationMessages(conversationId);
  const { mutate: ask, isPending, error, reset: resetError } = useAskAssistant();

  // Not answered yet (pending) or not answered at all (failed): never part of the saved conversation.
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [failedQuestion, setFailedQuestion] = useState<string | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  useEffect(() => {
    setFailedQuestion(null);
    resetError();
  }, [conversationId, resetError]);

  // Purged after a month, or a stale link.
  const isConversationMissing = Boolean(conversationId) && !isConversationLoading && conversation === null;

  function send(text: string) {
    if (!currentSpace?.id || isPending) {
      return;
    }
    const spaceId = currentSpace.id;
    setPendingQuestion(text);
    setFailedQuestion(null);
    resetError();

    ask(
      { spaceId, conversationId, question: text },
      {
        onSuccess: (response) => {
          queryClient.setQueryData<AiChatMessage[]>(keys.messages(response.conversationId), (current = []) => [
            ...current,
            createMessage('user', text),
            createMessage('assistant', response.answer, response.sources),
          ]);
          if (response.conversationId !== conversationId) {
            // Known right away, so the new page never looks like a purged conversation.
            queryClient.setQueryData<AiConversation>(keys.conversation(response.conversationId), {
              id: response.conversationId,
              title: text,
              lastMessagePreview: null,
              updatedAt: new Date().toISOString(),
              createdBy: null,
              authorFirstName: null,
              authorLastName: null,
            });
          }
          void queryClient.invalidateQueries({ queryKey: keys.conversation(response.conversationId) });
          void queryClient.invalidateQueries({ queryKey: keys.conversations(spaceId) });
          if (response.conversationId !== conversationId) {
            navigate(spacePath(`/assistant/${response.conversationId}`));
          }
        },
        onError: () => {
          setFailedQuestion(text);
        },
        onSettled: () => {
          setPendingQuestion(null);
        },
      },
    );
  }

  function handleSelectConversation(id: string) {
    setIsHistoryOpen(false);
    navigate(spacePath(`/assistant/${id}`));
  }

  function handleNewConversation() {
    setIsHistoryOpen(false);
    navigate(spacePath('/assistant'));
  }

  // Panes swap in place on phones: start each one at its top, where its header is.
  function showHistory(isOpen: boolean) {
    setIsHistoryOpen(isOpen);
    window.scrollTo({ top: 0 });
  }

  function handleCloseHistory(event: MouseEvent<HTMLElement>) {
    event.preventDefault();
    showHistory(false);
  }

  function handleRetry() {
    if (failedQuestion) {
      send(failedQuestion);
    }
  }

  return {
    conversationId,
    title: isCompact
      ? shortenTitle(conversation?.title ?? NEW_CONVERSATION_TITLE, COMPACT_TITLE_MAX_LENGTH)
      : conversation?.title ?? NEW_CONVERSATION_TITLE,
    messages,
    pendingQuestion,
    failedQuestion,
    hasConversation: messages.length > 0 || pendingQuestion !== null || failedQuestion !== null,
    isMessagesLoading: Boolean(conversationId) && isMessagesLoading,
    isConversationMissing,
    isPending,
    errorMessage: error?.message ?? null,
    isAiEnabled,
    isSettingsLoading,
    isCompact,
    // Below `md` one column at a time: the conversation, or the history when asked.
    showDetail: !isHistoryOpen,
    // Brings the newest message, or the pending answer, into view.
    scrollKey: `${conversationId ?? 'new'}-${messages.length}-${pendingQuestion ?? ''}-${failedQuestion ?? ''}`,
    handleSubmit: send,
    handleRetry,
    handleSelectConversation,
    handleNewConversation,
    conversationPath: spacePath(conversationId ? `/assistant/${conversationId}` : '/assistant'),
    historyButtonLabel: conversations?.length ? `Historique (${conversations.length})` : 'Historique',
    handleOpenHistory: () => showHistory(true),
    handleCloseHistory,
  };
}
